import React from 'react';
import { cn } from '@core/utils/cn';
import LanguageSelect, { TLanguage } from '../language-select';
import { useLocalizedField } from '@components/hooks/use-localised-field';
import { TextArea, TextAreaProps } from '.';
import { I18NText } from '@api/ChallengeClient';
import { I18n } from '@core/i18n';

interface LocalizedTextAreaProps
    extends Omit<TextAreaProps, 'value' | 'onChange'> {
    value: I18NText;
    disabled?: boolean;
    resetKey?: number;
    onChange(value: I18NText): void;
}

const LocalizedTextArea = ({
    value,
    onChange,
    disabled,
    resetKey = 0,
    ...props
}: LocalizedTextAreaProps) => {
    const { options, language, setLanguage } = useLocalizedField(
        value,
        I18n.allLanguageOptions,
        I18n.mainLanguageKey
    );
    if (!Object.prototype.hasOwnProperty.call(value, I18n.mainLanguageKey)) {
        onChange({
            ...value,
            [I18n.mainLanguageKey]: ''
        });
    }

    function handleLanguageChange(nextLanguage: TLanguage) {
        setLanguage(nextLanguage);

        if (!Object.prototype.hasOwnProperty.call(value, nextLanguage)) {
            onChange({
                ...value,
                [nextLanguage]: ''
            });
        }
    }

    function handleOnChange(e: string, key: string) {
        onChange({ ...value, [key]: e });
    }

    return (
        <div className="relative" data-testid={`${props.dataTestId}-language`}>
            <div className="absolute top-2.5 right-4 z-[8]">
                <LanguageSelect
                    options={options}
                    value={language}
                    onChange={handleLanguageChange}
                />
            </div>

            {value &&
                Object.entries(value).map(([key, value]) => (
                    <div
                        className={cn(language !== key && 'hidden')}
                        key={key}
                        data-testid={`${key}-sponsor-description-field`}
                    >
                        <TextArea
                            {...props}
                            key={`${key}-${resetKey}`}
                            value={value || ''}
                            onChange={(e) => {
                                handleOnChange(e, key);
                            }}
                            disabled={disabled}
                        />
                    </div>
                ))}
        </div>
    );
};

export default LocalizedTextArea;
